// ========= Copyright 2025-2026 @ Eigent.ai All Rights Reserved. =========
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
//     http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.
// ========= Copyright 2025-2026 @ Eigent.ai All Rights Reserved. =========

import { skillImportZip } from '@/api/brain';
import ConfirmModal from '@/components/ui/alertDialog';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogContentSection,
  DialogHeader,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { recordFeatureUsed } from '@/lib/events/appEvents';
import { buildSkillMd, parseSkillMd } from '@/lib/skillToolkit';
import { useSkillsStore } from '@/store/skillsStore';

import { DsIcon } from '@/components/ui/ds-icon';
import { AlertCircle, File, Upload, X } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

/** Guard renderer memory when sending zip bytes to the main process. */
const MAX_SKILL_ZIP_IMPORT_BYTES = 50 * 1024 * 1024;

interface SkillUploadDialogProps {
  open: boolean;
  onClose: () => void;
  /** File upload vs compose SKILL.md in the editor */
  mode?: 'upload' | 'create';
  /** Same-name replace keeps `skillDirName` stable; bump the document preview. */
  onPackageWritten?: () => void;
}

export default function SkillUploadDialog({
  open,
  onClose,
  mode = 'upload',
  onPackageWritten,
}: SkillUploadDialogProps) {
  const { t } = useTranslation();
  const { addSkill, syncFromDisk } = useSkillsStore();
  const [isDragging, setIsDragging] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [fileContent, setFileContent] = useState<string>('');
  const [_isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const confirmingConflictRef = useRef(false);
  const [isZip, setIsZip] = useState(false);
  const [uploadError, setUploadError] = useState<'invalid_format' | null>(null);
  const [conflictDialog, setConflictDialog] = useState<{
    open: boolean;
    folderName: string;
    skillName: string;
  } | null>(null);
  const [pendingConflicts, setPendingConflicts] = useState<
    Array<{ folderName: string; skillName: string }>
  >([]);
  const [confirmedReplacements, setConfirmedReplacements] = useState<
    Set<string>
  >(new Set());
  const [pendingFileBuffer, setPendingFileBuffer] =
    useState<ArrayBuffer | null>(null);
  const [composeContent, setComposeContent] = useState('');
  const [savingCompose, setSavingCompose] = useState(false);

  useEffect(() => {
    if (!open || mode !== 'create') return;
    setComposeContent(
      buildSkillMd(
        t('agents.create-skill-default-name'),
        t('agents.create-skill-default-description'),
        '## Instructions\n\n'
      )
    );
  }, [open, mode, t]);

  const handleClose = useCallback(() => {
    setSelectedFile(null);
    setFileContent('');
    setIsDragging(false);
    setIsZip(false);
    setUploadError(null);
    setConflictDialog(null);
    setPendingConflicts([]);
    setConfirmedReplacements(new Set());
    setPendingFileBuffer(null);
    setComposeContent('');
    setSavingCompose(false);
    onClose();
  }, [onClose]);

  const handleSaveCompose = useCallback(async () => {
    const meta = parseSkillMd(composeContent);
    if (!meta) {
      toast.error(t('agents.upload-error-invalid-yaml'));
      return;
    }
    setSavingCompose(true);
    try {
      await addSkill({
        name: meta.name,
        description: meta.description,
        filePath: 'SKILL.md',
        fileContent: composeContent,
        scope: { isGlobal: true, selectedAgents: [] },
        enabled: true,
      });
      toast.success(t('agents.skill-added-success'));
      recordFeatureUsed('skills', { action: 'create' });
      onPackageWritten?.();
      handleClose();
    } catch {
      toast.error(t('agents.skill-add-error'));
    } finally {
      setSavingCompose(false);
    }
  }, [addSkill, composeContent, handleClose, onPackageWritten, t]);

  /**
   * The package is already written to disk by the time this runs, so a failed
   * refresh means a stale list — never a failed import. Reporting it as
   * `skill-add-error` would send the user back to re-import a skill they
   * already have.
   */
  const refreshAfterImport = useCallback(async () => {
    try {
      await syncFromDisk();
    } catch (error) {
      console.warn('[Skills] Refresh after import failed:', error);
      toast.warning(t('agents.library-global-load-failed'));
    }
    onPackageWritten?.();
  }, [onPackageWritten, syncFromDisk, t]);

  const resetConflictState = useCallback(() => {
    setConflictDialog(null);
    setPendingConflicts([]);
    setConfirmedReplacements(new Set());
    setPendingFileBuffer(null);
  }, []);

  const handleConflictConfirm = useCallback(async () => {
    if (!conflictDialog) return;
    confirmingConflictRef.current = true;
    try {
      const { folderName } = conflictDialog;
      const newConfirmed = new Set(confirmedReplacements);
      newConfirmed.add(folderName);
      setConfirmedReplacements(newConfirmed);

      // Remove current conflict from pending list
      const remaining = pendingConflicts.filter(
        (c) => c.folderName !== folderName
      );
      setPendingConflicts(remaining);

      // If more conflicts, show next one
      if (remaining.length > 0) {
        setConflictDialog({
          open: true,
          folderName: remaining[0].folderName,
          skillName: remaining[0].skillName,
        });
      } else {
        // All conflicts handled, proceed with import
        setConflictDialog(null);
        if (!pendingFileBuffer) {
          resetConflictState();
          return;
        }

        if (pendingFileBuffer.byteLength > MAX_SKILL_ZIP_IMPORT_BYTES) {
          toast.error(t('agents.zip-import-too-large'));
          resetConflictState();
          return;
        }

        try {
          const result = await skillImportZip(
            pendingFileBuffer,
            Array.from(newConfirmed)
          );

          if (!result?.success) {
            toast.error(result?.error || t('agents.skill-add-error'));
            resetConflictState();
            return;
          }

          await refreshAfterImport();
          toast.success(t('agents.skill-added-success'));
        } catch {
          toast.error(t('agents.skill-add-error'));
        }
        resetConflictState();
      }
    } finally {
      confirmingConflictRef.current = false;
    }
  }, [
    conflictDialog,
    confirmedReplacements,
    pendingConflicts,
    pendingFileBuffer,
    resetConflictState,
    refreshAfterImport,
    t,
  ]);

  const handleConflictCancel = useCallback(async () => {
    if (!conflictDialog || confirmingConflictRef.current) return;

    // Remove current conflict from pending list (user skipped this one)
    const remaining = pendingConflicts.filter(
      (c) => c.folderName !== conflictDialog.folderName
    );
    setPendingConflicts(remaining);

    // If more conflicts, show next one
    if (remaining.length > 0) {
      setConflictDialog({
        open: true,
        folderName: remaining[0].folderName,
        skillName: remaining[0].skillName,
      });
    } else {
      // All conflicts handled, proceed with import
      setConflictDialog(null);
      if (!pendingFileBuffer) {
        resetConflictState();
        return;
      }

      if (confirmedReplacements.size === 0) {
        // The initial import may already have installed non-conflicting skills.
        // Refresh even when every conflict was skipped so those skills appear.
        await refreshAfterImport();
        resetConflictState();
        return;
      }

      if (pendingFileBuffer.byteLength > MAX_SKILL_ZIP_IMPORT_BYTES) {
        toast.error(t('agents.zip-import-too-large'));
        resetConflictState();
        return;
      }

      try {
        const result = await skillImportZip(
          pendingFileBuffer,
          Array.from(confirmedReplacements)
        );

        if (!result?.success) {
          toast.error(result?.error || t('agents.skill-add-error'));
          resetConflictState();
          return;
        }

        await refreshAfterImport();
        toast.success(t('agents.skill-added-success'));
      } catch {
        toast.error(t('agents.skill-add-error'));
      }
      resetConflictState();
    }
  }, [
    conflictDialog,
    confirmedReplacements,
    pendingConflicts,
    pendingFileBuffer,
    resetConflictState,
    refreshAfterImport,
    t,
  ]);

  const handleUpload = useCallback(
    async (
      fileArg?: File,
      options?: { isZipOverride?: boolean; contentOverride?: string }
    ) => {
      const fileToUse = fileArg ?? selectedFile;
      if (!fileToUse) return;

      const isZipToUse = options?.isZipOverride ?? isZip;
      const fileContentToUse = options?.contentOverride ?? fileContent;

      setIsUploading(true);
      try {
        // Zip import: Brain REST (Web + Electron) or IPC fallback (Electron only)
        if (isZipToUse) {
          let buffer: ArrayBuffer;
          try {
            buffer = await fileToUse.arrayBuffer();
          } catch {
            toast.error(t('agents.file-read-error'));
            return;
          }

          if (buffer.byteLength > MAX_SKILL_ZIP_IMPORT_BYTES) {
            toast.error(t('agents.zip-import-too-large'));
            return;
          }

          // First, check for conflicts
          const result = await skillImportZip(buffer);

          if (result?.conflicts && result.conflicts.length > 0) {
            // Store conflicts and show dialog for first conflict
            setPendingConflicts(result.conflicts);
            setPendingFileBuffer(buffer);
            setConflictDialog({
              open: true,
              folderName: result.conflicts[0].folderName,
              skillName: result.conflicts[0].skillName,
            });
            // Reset file state and close the main upload dialog
            setSelectedFile(null);
            setFileContent('');
            if (fileInputRef.current) fileInputRef.current.value = '';
            onClose();
            return;
          }

          if (!result?.success) {
            toast.error(result?.error || t('agents.skill-add-error'));
            return;
          }

          await refreshAfterImport();
          toast.success(t('agents.skill-added-success'));
          recordFeatureUsed('skills', { action: 'upload', format: 'zip' });
          handleClose();
          return;
        }

        if (!fileContentToUse) return;

        const fileName = fileToUse.name.replace(/\.[^/.]+$/, '');

        // Prefer SKILL.md frontmatter (name + description) at upload time
        const meta = parseSkillMd(fileContentToUse);
        let name = meta?.name ?? fileName;
        let description = meta?.description ?? '';

        // Fallback: no frontmatter — use first heading and first paragraph
        if (!meta && fileContentToUse.startsWith('#')) {
          const lines = fileContentToUse.split('\n');
          const headingMatch = lines[0].match(/^#\s+(.+)/);
          if (headingMatch) name = headingMatch[1];
          for (let i = 1; i < lines.length; i++) {
            const line = lines[i].trim();
            if (line && !line.startsWith('#')) {
              description = line;
              break;
            }
          }
        }

        await addSkill({
          name,
          description: description || t('agents.custom-skill'),
          filePath: fileToUse.name,
          fileContent: fileContentToUse,
          scope: { isGlobal: true, selectedAgents: [] },
          enabled: true,
        });

        toast.success(t('agents.skill-added-success'));
        recordFeatureUsed('skills', { action: 'upload', format: 'md' });
        onPackageWritten?.();
        handleClose();
      } catch (_error) {
        toast.error(t('agents.skill-add-error'));
      } finally {
        setIsUploading(false);
      }
    },
    [
      addSkill,
      fileContent,
      handleClose,
      isZip,
      onClose,
      selectedFile,
      refreshAfterImport,
      t,
      onPackageWritten,
    ]
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const processFile = useCallback(
    async (file: File) => {
      // Only .zip or skill package (.skill, .md) are valid
      const skillPackageExtensions = ['.zip', '.skill', '.md'];
      const extension = file.name
        .substring(file.name.lastIndexOf('.'))
        .toLowerCase();

      if (!skillPackageExtensions.includes(extension)) {
        setSelectedFile(file);
        setUploadError('invalid_format');
        return;
      }

      try {
        setUploadError(null);

        // Detect if file is a zip archive: .zip always, .skill by magic bytes
        let treatAsZip = extension === '.zip';
        if (extension === '.skill') {
          const header = new Uint8Array(await file.slice(0, 4).arrayBuffer());
          // ZIP magic bytes: PK\x03\x04
          if (
            header[0] === 0x50 &&
            header[1] === 0x4b &&
            header[2] === 0x03 &&
            header[3] === 0x04
          ) {
            treatAsZip = true;
          }
        }

        const maxBytes = treatAsZip ? MAX_SKILL_ZIP_IMPORT_BYTES : 1024 * 1024;
        if (file.size > maxBytes) {
          toast.error(
            t(
              treatAsZip
                ? 'agents.zip-import-too-large'
                : 'agents.file-too-large'
            )
          );
          return;
        }

        setSelectedFile(file);

        if (treatAsZip) {
          setIsZip(true);
          setFileContent('');
          await handleUpload(file, {
            isZipOverride: true,
            contentOverride: '',
          });
        } else {
          const content = await file.text();
          setIsZip(false);
          setFileContent(content);
          // Let handleUpload's fallback logic handle files without frontmatter
          // (it extracts name from # heading or filename)
          await handleUpload(file, {
            isZipOverride: false,
            contentOverride: content,
          });
        }
      } catch (_error) {
        toast.error(t('agents.file-read-error'));
      }
    },
    [handleUpload, t]
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);
      const files = e.dataTransfer.files;
      if (files.length > 0) {
        processFile(files[0]);
      }
    },
    [processFile]
  );

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      processFile(files[0]);
    }
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    setFileContent('');
    setUploadError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const errorMessage =
    uploadError === 'invalid_format'
      ? t('agents.upload-error-invalid-format')
      : null;

  return (
    <>
      <Dialog open={open} onOpenChange={(isOpen) => !isOpen && handleClose()}>
        <DialogContent
          size={mode === 'create' ? 'md' : 'sm'}
          aria-describedby={undefined}
          showCloseButton
          onClose={handleClose}
          overlayVariant="dimmed"
        >
          <DialogHeader
            title={
              mode === 'create'
                ? t('agents.create-skill')
                : t('agents.add-skill')
            }
          />
          <DialogContentSection>
            <div className="flex flex-col gap-4">
              {mode === 'create' ? (
                <>
                  <span className="block text-ds-text-base text-ds-ink-muted-default">
                    {t('agents.compose-skill-hint')}
                  </span>
                  <Textarea
                    variant="none"
                    value={composeContent}
                    onChange={(e) => setComposeContent(e.target.value)}
                    className="min-h-[220px] resize-y font-mono text-ds-text-base"
                    spellCheck={false}
                  />
                  <div className="flex justify-end gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      type="button"
                      onClick={handleClose}
                    >
                      {t('layout.cancel')}
                    </Button>
                    <Button
                      variant="primary"
                      size="sm"
                      type="button"
                      disabled={savingCompose || !composeContent.trim()}
                      onClick={() => void handleSaveCompose()}
                    >
                      {t('agents.save-skill')}
                    </Button>
                  </div>
                </>
              ) : null}
              {mode === 'upload' ? (
                <div
                  className={`relative cursor-pointer rounded-xl border-2 border-dashed p-8 transition-colors duration-200 ease-[cubic-bezier(0.23,1,0.32,1)] ${
                    uploadError
                      ? 'border-ds-border-status-error-default-default bg-ds-bg-status-error-subtle-default'
                      : isDragging
                        ? 'border-ds-ring-focus bg-ds-neutral-strong-default'
                        : 'border-ds-hairline-default-default hover:border-ds-hairline-strong-default hover:bg-ds-neutral-default-default'
                  }`}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  onClick={() => fileInputRef.current?.click()}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".skill,.md,.zip"
                    onChange={handleFileSelect}
                    className="hidden"
                  />

                  {selectedFile ? (
                    <div className="flex flex-col items-center gap-6">
                      <div className="flex items-center gap-2">
                        <div
                          className={`flex shrink-0 items-center justify-center rounded-lg p-1 ${
                            uploadError
                              ? 'bg-ds-bg-status-error-subtle-default'
                              : 'bg-ds-neutral-strong-default'
                          }`}
                        >
                          <DsIcon
                            icon={File}
                            className={`${uploadError ? 'text-ds-icon-status-error-default-default' : 'text-ds-ink-default-default'}`}
                          />
                        </div>
                        <div className="flex w-full min-w-0 flex-col">
                          <span
                            className={`truncate text-ds-text-base font-medium ${
                              uploadError
                                ? 'text-ds-text-status-error-strong-default'
                                : 'text-ds-ink-default-default'
                            }`}
                          >
                            {selectedFile.name}
                          </span>
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRemoveFile();
                          }}
                        >
                          <DsIcon icon={X} />
                        </Button>
                      </div>

                      <span
                        className={`text-ds-text-base ${
                          uploadError
                            ? 'text-ds-text-status-error-strong-default'
                            : 'text-ds-ink-muted-default'
                        }`}
                      >
                        {uploadError
                          ? t('agents.reupload-file')
                          : `${(selectedFile.size / 1024).toFixed(1)} KB`}
                      </span>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center gap-2">
                      <div className="flex h-12 w-12 items-center justify-center">
                        <DsIcon
                          icon={Upload}
                          className="text-ds-ink-muted-default"
                        />
                      </div>
                      <div className="flex flex-col items-center gap-1 text-center">
                        <span className="text-ds-text-base font-medium text-ds-ink-default-default">
                          {t('agents.drag-and-drop')}
                        </span>
                        <span className="mt-1 text-ds-text-base text-ds-ink-muted-default">
                          {t('agents.or-click-to-browse')}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              ) : null}

              {/* Error notice */}
              {mode === 'upload' && uploadError && errorMessage && (
                <div
                  className="flex items-center gap-4 rounded-xl border border-x border-y border-ds-border-status-error-default-default bg-ds-bg-status-error-subtle-default px-4 py-3"
                  role="alert"
                >
                  <DsIcon
                    icon={AlertCircle}
                    className="shrink-0 text-ds-icon-status-error-default-default"
                  />
                  <span className="text-ds-text-base text-ds-text-status-error-strong-default">
                    {errorMessage}
                  </span>
                </div>
              )}

              {/* File Requirements */}
              {mode === 'upload' ? (
                <div className="rounded-xl bg-ds-neutral-default-default p-4">
                  <span className="text-ds-text-base font-bold text-ds-ink-default-default">
                    {t('agents.file-requirements')}
                  </span>
                  <span className="mt-2 flex items-start gap-2 text-ds-text-base text-ds-ink-muted-default">
                    <span className="text-ds-ink-muted-default">•</span>
                    <span>{t('agents.file-requirements-detail-1')}</span>
                  </span>
                  <span className="mt-1 flex items-start gap-2 text-ds-text-base text-ds-ink-muted-default">
                    <span className="text-ds-ink-muted-default">•</span>
                    <span>{t('agents.file-requirements-detail-2')}</span>
                  </span>
                </div>
              ) : null}
            </div>
          </DialogContentSection>
        </DialogContent>
      </Dialog>

      {/* Conflict confirmation dialog - rendered outside main dialog */}
      {conflictDialog && (
        <ConfirmModal
          isOpen={conflictDialog.open}
          onClose={handleConflictCancel}
          onConfirm={handleConflictConfirm}
          title={t('agents.replace-skill-title', {
            name: conflictDialog.skillName,
          })}
          message={t('agents.replace-skill-message')}
          confirmText={t('agents.update-and-replace')}
          cancelText={t('layout.cancel')}
          confirmVariant="primary"
          confirmTone="error"
        />
      )}
    </>
  );
}
